import React from 'react'

import { FormattedMessage, IntlShape } from 'react-intl'

import { AnalyticsAudienceReportingChartsDoughnut } from '@components/Analytics/AudienceReporting/Charts'
import { useIntl } from '@context/Internationalization/useIntl'
import Theme from '@theme'
import { HorizontalBar } from '@tribegroup/Charts'
import { IAgentSourcedCreator, IPerformanceGrowthPoint, IPerformanceMonthlyPoint } from '../types'
import { IAudienceCredibility } from '../types'
import {
  ChartCard,
  ChartGrid,
  ChartTitle,
  PanelRoot,
  SectionTitle,
  SparklineGrid,
  StatGrid,
} from './AudiencePanel.styled'
import Sparkline from './Sparkline'
import StatCard, { formatValue } from './StatCard'

interface IProps {
  creator: IAgentSourcedCreator
}

interface IChartDatum {
  label: string
  value: number
  percentage: number
}

const toChartData = <T extends { weight: number }>(
  items: ReadonlyArray<T> | undefined,
  labelOf: (item: T) => string,
  limit?: number,
): IChartDatum[] => {
  const list = items ?? []
  const sliced = limit ? list.slice(0, limit) : list
  return sliced.map((it) => ({
    label: labelOf(it),
    value: it.weight,
    percentage: it.weight * 100,
  }))
}

// Credibility is reported per audience segment (likers / followers / commenters)
// as a scalar score (0..1). Render one horizontal bar per segment, in this fixed
// order, for every segment that actually carries a score.
const CREDIBILITY_SEGMENTS: ReadonlyArray<{
  key: 'likers' | 'followers' | 'commenters'
  labelKey: string
}> = [
  { key: 'likers', labelKey: 'creatorSearch.drawer.credibility.likers' },
  { key: 'followers', labelKey: 'creatorSearch.drawer.credibility.followers' },
  { key: 'commenters', labelKey: 'creatorSearch.drawer.credibility.commenters' },
]

// A credibility score is only ever meaningful in [0, 1]. Reject non-finite input
// (NaN, +/-Infinity, or anything that isn't a number at runtime — the network
// payload isn't statically typechecked) rather than let it flow through as
// `NaN%`, and clamp genuinely out-of-range values (e.g. a `1.5` rounding glitch)
// into range instead of rendering `150%`/negative bars.
const clampScore = (value: number): number | null =>
  Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : null

// Credibility arrives as EITHER a bare scalar score (0..1, today's payload) OR
// an object { score? } (richer payload). Normalise both into the object shape,
// validating/clamping the score, so the read path only deals with one form and
// never surfaces a degenerate value.
const normalizeCredibility = (
  raw: IAudienceCredibility | number | null | undefined,
): IAudienceCredibility | null => {
  if (raw == null) return null
  const rawScore = typeof raw === 'number' ? raw : raw.score
  const score = typeof rawScore === 'number' ? clampScore(rawScore) : null
  return score == null ? null : { score }
}

// "YYYY-MM" -> locale-aware short month name ("Dec"). Falls back to the raw
// string for unparseable input so a bad month never crashes the chart.
const monthLabel = (month: string, intl: IntlShape): string => {
  const [year, monthNum] = month.split('-').map(Number)
  if (!year || !monthNum || monthNum < 1 || monthNum > 12) return month
  return intl.formatDate(new Date(Date.UTC(year, monthNum - 1, 1)), {
    month: 'short',
    timeZone: 'UTC',
  })
}

const latestValueOf = (points: ReadonlyArray<{ value: number | null }>): string | undefined =>
  points.length ? formatValue(points[points.length - 1].value) : undefined

const AudiencePanel: React.FC<IProps> = ({ creator }) => {
  const { intl } = useIntl()
  const { audience, performance: perf } = creator
  // Likers can be null on YouTube; fall back to followers, then commenters.
  const segment = audience?.likers ?? audience?.followers ?? audience?.commenters ?? null
  const overall = audience?.overall ?? null

  // Build sparkline points from a month-keyed history, mapping missing values to
  // null (not 0) so the chart can tell "no data" apart from a real zero.
  const toPoints = <T extends { month: string }>(
    history: ReadonlyArray<T> | undefined,
    valueOf: (item: T) => number | null | undefined,
  ) => (history ?? []).map((p) => ({ label: monthLabel(p.month, intl), value: valueOf(p) ?? null }))

  // All three sparklines share the overall monthly series in growth_history,
  // which carries followers, avg_views and avg_likes (and the month-level nulls
  // surfaced here) so the charts agree with each other. seriesFor prefers the
  // growth_history value for a metric and falls back to the stats_by_type "all"
  // history only when growth_history never populates it (e.g. reels plays).
  const growth = perf?.growth_history
  const allStats = perf?.stats_by_type?.find((s) => s.post_type === 'all')
  const seriesFor = (
    growthValue: (p: IPerformanceGrowthPoint) => number | null | undefined,
    monthlyValue: (p: IPerformanceMonthlyPoint) => number | null | undefined,
  ) =>
    growth?.some((p) => growthValue(p) != null)
      ? toPoints(growth, growthValue)
      : toPoints(allStats?.monthly_history, monthlyValue)

  const followersHistory = toPoints(growth, (p) => p.followers)
  const viewsHistory = seriesFor(
    (p) => p.avg_views,
    (p) => p.avg_views ?? p.avg_reels_plays,
  )
  const likesHistory = seriesFor(
    (p) => p.avg_likes,
    (p) => p.avg_likes,
  )

  const genderFemale = segment?.demographics?.gender?.female ?? overall?.gender?.female ?? 0
  const genderMale = segment?.demographics?.gender?.male ?? overall?.gender?.male ?? 0
  const genderData: IChartDatum[] = [
    { label: 'Female', value: genderFemale, percentage: genderFemale * 100 },
    { label: 'Male', value: genderMale, percentage: genderMale * 100 },
  ]
  const isGenderEmpty = !segment?.demographics?.gender && !overall?.gender

  const ageData = toChartData(
    segment?.demographics?.age_groups ?? overall?.age_groups ?? undefined,
    (g) => g.range,
  )
  const locationData = toChartData(
    segment?.geo?.top_countries ?? overall?.top_countries ?? undefined,
    (c) => c.name,
    5,
  )
  const languageData = toChartData(
    segment?.demographics?.top_languages ?? undefined,
    (l) => l.name,
    5,
  )
  // Prefer segment.interests (weight-based). If absent OR empty, derive from
  // overall.affinity_interests (share-based, same scale 0..1). Note: an empty
  // array is truthy, so we must check length, not just presence.
  const interestData: IChartDatum[] = segment?.interests?.length
    ? toChartData(
        segment.interests.map((i) => ({ name: i.name, weight: i.weight ?? i.share ?? 0 })),
        (i) => i.name,
        5,
      )
    : (overall?.affinity_interests ?? []).slice(0, 5).map((i) => ({
        label: i.name,
        value: i.share,
        percentage: i.share * 100,
      }))

  // One bar per audience segment that reports a credibility score (0..1).
  // normalizeCredibility accepts today's bare scalar or the richer { score }
  // object; segments without a score are omitted so an empty list lets the chart
  // fall back to its own empty state.
  const credibilityData: IChartDatum[] = CREDIBILITY_SEGMENTS.flatMap(({ key, labelKey }) => {
    const score = normalizeCredibility(audience?.[key]?.credibility)?.score
    return score != null
      ? [{ label: intl.formatMessage({ id: labelKey }), value: score, percentage: score * 100 }]
      : []
  })

  return (
    <PanelRoot>
      <SectionTitle>
        <FormattedMessage id="creatorSearch.drawer.audience.title" />
      </SectionTitle>

      <StatGrid>
        <StatCard labelKey="creatorSearch.drawer.stat.followers" value={perf?.followers} />
        <StatCard
          labelKey="creatorSearch.drawer.stat.avgViews"
          value={perf?.avg_views ?? perf?.avg_reels_plays}
        />
        <StatCard
          labelKey="creatorSearch.drawer.stat.engagementRate"
          value={
            perf?.engagement_rate != null ? `${(perf.engagement_rate * 100).toFixed(2)}%` : null
          }
        />
        <StatCard labelKey="creatorSearch.drawer.stat.avgLikes" value={perf?.avg_likes} />
        <StatCard labelKey="creatorSearch.drawer.stat.avgComments" value={perf?.avg_comments} />
        <StatCard labelKey="creatorSearch.drawer.stat.avgShares" value={perf?.avg_shares} />
      </StatGrid>

      <SparklineGrid>
        <Sparkline
          labelKey="creatorSearch.drawer.stat.followers"
          points={followersHistory}
          latestValue={latestValueOf(followersHistory)}
        />
        <Sparkline
          labelKey="creatorSearch.drawer.stat.avgViews"
          points={viewsHistory}
          latestValue={latestValueOf(viewsHistory)}
        />
        <Sparkline
          labelKey="creatorSearch.drawer.stat.avgLikes"
          points={likesHistory}
          latestValue={latestValueOf(likesHistory)}
        />
      </SparklineGrid>

      <ChartGrid>
        <ChartCard>
          <ChartTitle>
            <FormattedMessage id="creatorSearch.drawer.audience.gender" />
          </ChartTitle>
          <AnalyticsAudienceReportingChartsDoughnut
            data={genderData}
            showAsPercentage
            isEmpty={isGenderEmpty}
          />
        </ChartCard>

        <ChartCard>
          <ChartTitle>
            <FormattedMessage id="creatorSearch.drawer.audience.ageRange" />
          </ChartTitle>
          <HorizontalBar
            theme={Theme}
            data={ageData}
            showAsPercentage
            showEmpty={ageData.length === 0}
          />
        </ChartCard>

        <ChartCard>
          <ChartTitle>
            <FormattedMessage id="creatorSearch.drawer.audience.topLocations" />
          </ChartTitle>
          <HorizontalBar
            theme={Theme}
            data={locationData}
            showAsPercentage
            showEmpty={locationData.length === 0}
          />
        </ChartCard>

        <ChartCard>
          <ChartTitle>
            <FormattedMessage id="creatorSearch.drawer.audience.language" />
          </ChartTitle>
          <HorizontalBar
            theme={Theme}
            data={languageData}
            showAsPercentage
            showEmpty={languageData.length === 0}
          />
        </ChartCard>

        <ChartCard>
          <ChartTitle>
            <FormattedMessage id="creatorSearch.drawer.audience.interests" />
          </ChartTitle>
          <HorizontalBar
            theme={Theme}
            data={interestData}
            showAsPercentage
            showEmpty={interestData.length === 0}
          />
        </ChartCard>

        <ChartCard>
          <ChartTitle>
            <FormattedMessage id="creatorSearch.drawer.audience.credibility" />
          </ChartTitle>
          <HorizontalBar
            theme={Theme}
            data={credibilityData}
            showAsPercentage
            roundPercentage
            showEmpty={credibilityData.length === 0}
          />
        </ChartCard>
      </ChartGrid>
    </PanelRoot>
  )
}

export { AudiencePanel }
export default AudiencePanel
